import {
  CheckCircle2,
  AlertTriangle,
  Download,
  RotateCcw,
  ArrowRight,
  FileCheck,
  Users,
  Shield,
  Layers,
} from 'lucide-react';
import { useHistory } from 'react-router-dom';
import Button from '@components/ui/Button.js';
import SurfaceCard from '@components/ui/SurfaceCard.js';
import { cn } from '@utils/Cn.js';
import type { BulkImportResult } from '../types/index.js';

interface ImportSummaryCardProps {
  result: BulkImportResult;
  onDownloadReport: () => void;
  onImportAnother: () => void;
}

export default function ImportSummaryCard({
  result,
  onDownloadReport,
  onImportAnother,
}: ImportSummaryCardProps) {
  const history = useHistory();
  const hasFailures = result.failedCount > 0;

  return (
    <SurfaceCard
      className={cn(
        'p-8 border max-w-2xl mx-auto text-center space-y-6 animate-in zoom-in-95 duration-200',
        hasFailures
          ? 'border-amber-500/20 bg-amber-500/5'
          : 'border-green-500/20 bg-green-500/5',
      )}
    >
      {/* Icon & Title */}
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'flex h-16 w-16 items-center justify-center rounded-2xl mb-4',
            hasFailures
              ? 'bg-amber-500/10 text-amber-500'
              : 'bg-green-500/10 text-green-600 dark:text-green-400',
          )}
        >
          {hasFailures ? (
            <AlertTriangle size={36} strokeWidth={1.5} />
          ) : (
            <CheckCircle2 size={36} strokeWidth={1.5} />
          )}
        </div>
        <h2 className="text-lg font-black text-ink">
          {hasFailures ? 'Import Completed with Exceptions' : 'Import Completed Successfully!'}
        </h2>
        <p className="text-xs text-ink-faint mt-1 max-w-md">
          {hasFailures
            ? `Saved ${String(result.successCount)} valid policies to the database. Download the audit report to inspect the ${String(result.failedCount)} skipped row(s).`
            : `All ${String(result.successCount)} policies and client accounts have been securely saved to the database.`}
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl border border-line/40 bg-surface/80 p-4">
          <p className="text-2xl font-black text-green-600 dark:text-green-400">
            {result.createdPolicies}
          </p>
          <p className="text-[10px] font-bold text-ink-faint uppercase tracking-wider mt-1 flex items-center justify-center gap-1">
            <Shield size={11} /> Policies
          </p>
        </div>

        <div className="rounded-xl border border-line/40 bg-surface/80 p-4">
          <p className="text-2xl font-black text-ink">{result.createdClients}</p>
          <p className="text-[10px] font-bold text-ink-faint uppercase tracking-wider mt-1 flex items-center justify-center gap-1">
            <Users size={11} /> New Clients
          </p>
        </div>

        <div className="rounded-xl border border-line/40 bg-surface/80 p-4">
          <p className="text-2xl font-black text-blue-600 dark:text-blue-400">
            {result.associateCount ?? 0}
          </p>
          <p className="text-[10px] font-bold text-ink-faint uppercase tracking-wider mt-1 flex items-center justify-center gap-1">
            <Layers size={11} /> Associates
          </p>
        </div>

        <div className="rounded-xl border border-line/40 bg-surface/80 p-4">
          <p className="text-2xl font-black text-ink-faint">{result.duplicateCount}</p>
          <p className="text-[10px] font-bold text-ink-faint uppercase tracking-wider mt-1 flex items-center justify-center gap-1">
            <FileCheck size={11} /> Duplicates
          </p>
        </div>
      </div>

      {/* Reassurance Checklist */}
      <div className="rounded-xl border border-line/40 bg-surface/40 p-4 text-left space-y-2">
        <p className="text-[11px] font-bold text-ink uppercase tracking-wide">
          Automatic Cleaning &amp; Transformations Applied:
        </p>
        <ul className="text-xs text-ink-faint space-y-1 list-disc list-inside">
          <li>All Indian dates (DD-MM-YYYY) parsed and standardized into UTC timestamps</li>
          <li>Mobile numbers formatted with clean country code (+91)</li>
          <li>Corrupt unicode characters, non-breaking spaces, and leading symbols removed</li>
          <li>Motor vehicle and non-motor policies segregated without manual reformatting</li>
        </ul>
      </div>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
        <Button
          variant="primary"
          size="sm"
          onClick={onDownloadReport}
          leftIcon={<Download size={14} />}
          className="w-full sm:w-auto font-bold shadow-xs"
        >
          Download Audit Report (.xlsx)
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => { history.push('/policies'); }}
          rightIcon={<ArrowRight size={14} />}
          className="w-full sm:w-auto font-semibold"
        >
          View Policies
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={onImportAnother}
          leftIcon={<RotateCcw size={13} />}
          className="w-full sm:w-auto text-xs font-semibold text-ink-faint"
        >
          Import Another File
        </Button>
      </div>
    </SurfaceCard>
  );
}
